import { DateTime } from 'luxon'

import { getQuestAvailability } from '../constants/availability'

import type { Quest, QuestPlayerPhase, QuestRun } from './types'

export interface QuestPlayerState {
  phase: QuestPlayerPhase
  quest: Quest | null
  totemToken: string | null
  run: QuestRun | null
  errorMessage: string | null
}

export const initialQuestPlayerState: QuestPlayerState = {
  phase: 'loading',
  quest: null,
  totemToken: null,
  run: null,
  errorMessage: null
}

export const resolveQuestEntryPhase = (
  quest: Pick<Quest, 'start_at' | 'end_at'>,
  unlockRequired: boolean,
  now?: DateTime
): QuestPlayerPhase => {
  const availability = getQuestAvailability(quest, now)

  if (availability === 'soon') return 'standby'
  if (availability === 'closed') return 'closed'

  return unlockRequired ? 'totem' : 'welcome'
}

export type QuestPlayerAction =
  | { type: 'QUEST_LOAD_SUCCEEDED'; quest: Quest; totemToken: string | null; now?: DateTime }
  | { type: 'QUEST_LOAD_LOCKED' }
  | { type: 'QUEST_LOAD_FAILED'; blocked: boolean; message: string }
  | { type: 'START_REQUESTED' }
  | { type: 'RUN_CREATED'; run: QuestRun; hasQuizStage: boolean }
  | { type: 'TOTEM_UNLOCK_REQUIRED' }
  | { type: 'RUN_FINISHED'; run: QuestRun; nextPhase: 'choose_reward' | 'done' }
  | { type: 'REWARD_CHOSEN'; run: QuestRun }
  | { type: 'RESTART_FOR_NEXT_PARTICIPANT' }

export const questPlayerReducer = (
  state: QuestPlayerState,
  action: QuestPlayerAction
): QuestPlayerState => {
  switch (action.type) {
    case 'QUEST_LOAD_SUCCEEDED':
      return {
        ...state,
        phase: resolveQuestEntryPhase(action.quest, false, action.now),
        quest: action.quest,
        totemToken: action.totemToken,
        errorMessage: null
      }
    case 'QUEST_LOAD_LOCKED':
      return { ...state, phase: 'totem', quest: null, totemToken: null, errorMessage: null }
    case 'QUEST_LOAD_FAILED':
      return { ...state, phase: action.blocked ? 'blocked' : 'error', errorMessage: action.message }
    case 'START_REQUESTED':
      return state.phase === 'welcome' ? { ...state, phase: 'play' } : state
    case 'RUN_CREATED':
      return { ...state, run: action.run, phase: action.hasQuizStage ? 'quiz' : 'run' }
    case 'TOTEM_UNLOCK_REQUIRED':
      return { ...state, phase: 'totem', totemToken: null }
    case 'RUN_FINISHED':
      return { ...state, run: action.run, phase: action.nextPhase }
    case 'REWARD_CHOSEN':
      return { ...state, run: action.run, phase: 'done' }
    case 'RESTART_FOR_NEXT_PARTICIPANT':
      return { ...state, run: null, phase: 'welcome' }
    default:
      return state
  }
}
